import { styled, css } from '@vebox/etui-system';
import { useState, useEffect, createElement } from 'react';
import { b as _extends, d as _objectDestructuringEmpty, a as _objectSpread2, _ as _objectWithoutProperties } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Input, Div, Span } from '@vebox/etui-system-elements';
import { getCheckboxGlobalProps } from './utils.js';

const _excluded = ["getStyles"];
const Checkbox = styled(Input).attrs(_ref => {
  let props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return _objectSpread2({
    type: 'checkbox'
  }, props);
})`
  position: absolute;
  left: 0;
  z-index: 1;
  opacity: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;

    & + label {
      opacity: 0.65;
      &::before {
        background-color: #ddd;
        cursor: not-allowed;
      }
    }
  }

  &:focus + span,
  &:hover + span {
    border-color: ${({
  theme,
  disabled
}) => !disabled && theme.palette.primary.main};
  }

  ${_ref2 => {
  let {
      getStyles
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  return getStyles === null || getStyles === void 0 ? void 0 : getStyles(props);
}}
`;

const _excluded$1 = ["getStyles"];
const CheckboxDiv = styled(Div)`
  &:before,
  &:after {
    clear: both;
    display: table;
    content: '';
    line-height: 0;
  }

  white-space: nowrap;
  outline: none;
  display: inline-block;
  position: relative;
  line-height: 1;
  vertical-align: middle;
  ${_ref => {
  let {
      getStyles
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return getStyles === null || getStyles === void 0 ? void 0 : getStyles(props);
}}
`;

const _excluded$2 = ["checked", "disabled", "theme"],
  _excluded2 = ["getStyles"];
const CheckboxSpan = styled(Span).attrs(_ref => {
  let {
      checked,
      disabled,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return _objectSpread2({
    border: `1px solid ${checked && !disabled ? theme.palette.primary.main : checked && disabled ? theme.palette.action.disabledBackground : theme.palette.grey[60]}`,
    bg: theme.palette.common.white
  }, props);
})`
  position: relative;
  display: inline-block;
  top: 0;
  left: 0;
  border-radius: 2px;
  width: var(--size);
  height: var(--size);
  transition: border-color 0.15s;
  --size: 16px;

  ${({
  checked,
  disabled,
  theme
}) => {
  if (checked && !disabled) {
    return css`
        background-color: ${theme.palette.primary.main};
      `;
  }
  if (checked && disabled) {
    return css`
        background-color: ${theme.palette.action.disabledBackground};
      `;
  }
  if (disabled) {
    return css`
        border-color: ${theme.palette.action.disabledBackground};
        background-color: ${theme.palette.action.disabledBackground};
      `;
  }
  return ``;
}};

  &::after {
    content: ' ';
    position: absolute;
    top: 42%;
    left: 50%;
    border-radius: 2px;
    border-top: 0;
    border-left: 0;
    animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
    animation-duration: 0.3s;
    transform: translate(-50%, -50%) rotate(45deg);

    ${({
  checked,
  theme
}) => checked && css`
        width: 5px;
        height: 9px;
        border: 2px solid ${theme.palette.common.white};
        border-top: 0;
        border-left: 0;
      `};

    ${({
  indeterminate,
  checked,
  dashedIndeterminate,
  theme
}) => indeterminate && !checked && css`
        top: 50%;
        transform: translate(-50%, -50%);
        border: 0;
        border-radius: 0;
        ${dashedIndeterminate ? css`
              width: 70%;
              height: 25%;
              background-color: ${theme.palette.primary.main};
            ` : css`
              width: 70%;
              height: 70%;
              background-color: ${theme.palette.primary.main};
            `};
      `};

    ${({
  disabled,
  theme
}) => disabled && css`
        border: 2px solid ${theme.palette.action.disabled};
        border-top: 0;
        border-left: 0;
      `};
    ${props => !props.checked && !props.indeterminate && css`
        content: none;
      `};
  }

  ${_ref2 => {
  let {
      getStyles
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded2);
  return getStyles === null || getStyles === void 0 ? void 0 : getStyles(props);
}}
`;

const _excluded$3 = ["style", "name", "id", "disabled", "readOnly", "tabIndex", "onClick", "onFocus", "onBlur", "autoFocus", "value", "children", "testId", "indeterminate", "dashedIndeterminate", "styles", "checked", "onChange"];
const CheckboxBase = props => {
  const {
      style,
      name,
      id,
      disabled,
      readOnly,
      tabIndex,
      onClick,
      onFocus,
      onBlur,
      autoFocus,
      value,
      children,
      testId,
      indeterminate,
      dashedIndeterminate,
      styles = {},
      checked: defaultChecked,
      onChange
    } = props,
    rest = _objectWithoutProperties(props, _excluded$3);
  const globalProps = getCheckboxGlobalProps(rest);
  const [checked, setChecked] = useState(defaultChecked);
  const handleChange = e => {
    if (disabled) {
      return;
    }
    if (!defaultChecked) {
      setChecked(e.target.checked);
    }
    onChange(_objectSpread2(_objectSpread2({}, e), {}, {
      target: _objectSpread2(_objectSpread2(_objectSpread2({}, props), e.target), {}, {
        checked: e.target.checked
      }),
      stopPropagation() {
        e.stopPropagation();
      },
      preventDefault() {
        e.preventDefault();
      },
      nativeEvent: e.nativeEvent
    }));
  };
  useEffect(() => setChecked(defaultChecked), [defaultChecked]);
  return /*#__PURE__*/createElement(CheckboxDiv, {
    getStyles: styles.root
  }, /*#__PURE__*/createElement(Checkbox, _extends({
    name: name,
    id: id,
    readOnly: readOnly,
    disabled: disabled,
    tabIndex: tabIndex,
    checked: !!checked,
    onClick: onClick,
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: handleChange,
    autoFocus: autoFocus,
    value: value,
    style: style,
    testId: testId,
    getStyles: styles.checkboxInput
  }, globalProps)), /*#__PURE__*/createElement(CheckboxSpan, {
    checked: checked,
    disabled: disabled,
    indeterminate: indeterminate,
    dashedIndeterminate: dashedIndeterminate,
    getStyles: styles.checkboxSpan
  }));
};

export default CheckboxBase;
